import type { Selection } from '../input/tapToCommand';
import type { Sim } from '../sim/core/sim';
import type { BuildingDef } from '../sim/data/types';
import { buildProgress, isComplete } from '../sim/entities/building';
import type { Unit } from '../sim/entities/unit';
import type { PlacementRefusal } from '../sim/systems/construction';
import type { TrainRefusal } from '../sim/systems/production';

/**
 * What the rail shows for the current selection: a pure function of simulation state,
 * so it is unit-tested without Phaser or the DOM. Buttons carry actions as data; the
 * scene turns them into commands.
 */
export interface RailCard {
  readonly title: string;
  readonly lines: readonly string[];
  /** 0..1 progress of the item in production or construction, or null for no bar. */
  readonly progress: number | null;
}

export type RailAction =
  | { readonly kind: 'train'; readonly buildingId: number; readonly unitDefId: string }
  | { readonly kind: 'cancelTrain'; readonly buildingId: number }
  | { readonly kind: 'startPlacement'; readonly buildingDefId: string }
  | { readonly kind: 'confirmPlacement' }
  | { readonly kind: 'cancelPlacement' }
  | { readonly kind: 'stopAll' };

export interface RailButton {
  readonly id: string;
  readonly label: string;
  readonly enabled: boolean;
  readonly action: RailAction;
}

export interface RailState {
  readonly card: RailCard | null;
  readonly buttons: readonly RailButton[];
  /** Stop applies to selected units; disabled otherwise. */
  readonly stopEnabled: boolean;
}

export interface Placement {
  readonly def: BuildingDef;
  readonly at: { readonly x: number; readonly y: number } | null;
  readonly refusal: PlacementRefusal | null;
}

export interface Names {
  unit(defId: string): string;
  building(defId: string): string;
}

export const PLACEMENT_TEXT: Record<PlacementRefusal, string> = {
  'off-map': 'off the map',
  blocked: 'something is in the way',
  'unit-in-way': 'a unit is standing there',
  'no-gold': 'need gold',
  'no-lumber': 'need lumber',
};

export const REFUSAL_TEXT: Record<TrainRefusal, string> = {
  'not-built': 'not built yet',
  'cannot-train': 'not here',
  'queue-full': 'queue full',
  'no-gold': 'need gold',
  'no-lumber': 'need lumber',
  'no-supply': 'need supply',
};

function doing(u: Unit): string {
  switch (u.state.kind) {
    case 'gathering':
      return `Gathering ${u.state.target.kind === 'mine' ? 'gold' : 'lumber'}`;
    case 'moving':
      return 'Moving';
    case 'building':
      return 'Building';
    case 'attacking':
      return 'Attacking';
    case 'dying':
      return 'Down';
    case 'idle':
      return 'Idle';
  }
}

function plural(n: number, name: string): string {
  return `${n} ${name}${n > 1 ? 's' : ''}`;
}

export function railStateFor(
  sim: Sim,
  owner: number,
  selection: Selection,
  placing: Placement | null,
  names: Names,
): RailState {
  if (selection?.kind === 'units') {
    const units = selection.ids
      .map((id) => sim.getUnit(id))
      .filter((u): u is Unit => u !== undefined);
    if (units.length === 0) return { card: null, buttons: [], stopEnabled: false };

    if (placing) {
      const bname = names.building(placing.def.id);
      const status = !placing.at
        ? 'Tap the map to choose a spot'
        : placing.refusal
          ? `Cannot build here: ${PLACEMENT_TEXT[placing.refusal]}`
          : 'Good spot';
      return {
        card: { title: `Place ${bname}`, lines: [status], progress: null },
        buttons: [
          {
            id: 'build-confirm',
            label: 'Build here',
            enabled: placing.at !== null && placing.refusal === null,
            action: { kind: 'confirmPlacement' },
          },
          {
            id: 'build-cancel',
            label: 'Cancel',
            enabled: true,
            action: { kind: 'cancelPlacement' },
          },
        ],
        stopEnabled: true,
      };
    }

    const p = sim.player(owner);
    const builder = units.find((u) => u.def.builds.length > 0);
    const buildButtons: RailButton[] = (builder?.def.builds ?? []).flatMap((defId) => {
      const def = sim.buildingDef(defId);
      if (!def) return [];
      const affordable = p.gold >= def.cost.gold && p.lumber >= def.cost.lumber;
      return [
        {
          id: `build-${def.id}`,
          label: `Build ${names.building(def.id)} (${def.cost.gold} gold, ${def.cost.lumber} lumber)${affordable ? '' : ' — need resources'}`,
          enabled: affordable,
          action: { kind: 'startPlacement', buildingDefId: def.id },
        },
      ];
    });

    if (units.length === 1) {
      const u = units[0]!;
      const lines = [`HP ${Math.max(0, u.hp)}/${u.def.hp}`, doing(u)];
      if (u.carrying) lines.push(`Carrying ${u.carrying.amount} ${u.carrying.resource}`);
      return {
        card: { title: names.unit(u.def.id), lines, progress: null },
        buttons: buildButtons,
        stopEnabled: true,
      };
    }

    const counts = new Map<string, number>();
    for (const u of units) counts.set(u.def.id, (counts.get(u.def.id) ?? 0) + 1);
    const breakdown = [...counts].map(([id, n]) => plural(n, names.unit(id))).join(', ');
    return {
      card: { title: `${units.length} units`, lines: [breakdown], progress: null },
      buttons: buildButtons,
      stopEnabled: true,
    };
  }

  if (selection?.kind === 'building') {
    const b = sim.getBuilding(selection.id);
    if (!b) return { card: null, buttons: [], stopEnabled: false };
    const name = names.building(b.def.id);
    if (!isComplete(b)) {
      return {
        card: {
          title: name,
          lines: [
            `Under construction ${Math.round(buildProgress(b) * 100)}%`,
            'Tap it with a Hauler selected to build',
          ],
          progress: buildProgress(b),
        },
        buttons: [],
        stopEnabled: false,
      };
    }
    const front = b.queue[0];
    const lines = [
      front ? `Training ${names.unit(front.def.id)} (${b.queue.length} queued)` : 'Idle',
    ];
    const buttons: RailButton[] = b.def.trains.flatMap((unitDefId) => {
      const def = sim.unitDef(unitDefId);
      if (!def) return [];
      const refusal = sim.canTrain(b, def);
      const cost =
        def.cost.lumber > 0
          ? `${def.cost.gold} gold, ${def.cost.lumber} lumber`
          : `${def.cost.gold} gold`;
      return [
        {
          id: `train-${def.id}`,
          label: `Train ${names.unit(def.id)} (${cost})${refusal ? ` — ${REFUSAL_TEXT[refusal]}` : ''}`,
          enabled: refusal === null,
          action: { kind: 'train', buildingId: b.id, unitDefId },
        },
      ];
    });
    if (b.queue.length > 0)
      buttons.push({
        id: 'cancel-train',
        label: 'Cancel last',
        enabled: true,
        action: { kind: 'cancelTrain', buildingId: b.id },
      });
    const progress = front ? 1 - front.ticksLeft / front.def.trainTicks : null;
    return { card: { title: name, lines, progress }, buttons, stopEnabled: false };
  }

  return { card: null, buttons: [], stopEnabled: false };
}
